import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { CourseMaterialListItem } from 'components/slide-deck-material-components/CourseMaterialListItem'
import { Spinner } from 'components/Spinner'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { observer } from 'mobx-react-lite'
import { useId, useMemo } from 'react'
import { useTranslationTyped } from 'i18n/i18n'

export const PreWorkModal = observer(function PreWorkModal({
  cubit,
}: {
  cubit: StudentAssignmentCubit
}) {
  const { tt } = useTranslationTyped()
  const headingId = useId()
  const materials = cubit.availableStudentMaterials
  const libraryObject = cubit.libraryObject
  const preWorkMaterials = useMemo(
    () =>
      materials
        .filter((material) => material.isPreWork)
        .sort((a, b) => {
          // sort mp3 above pdf
          if (b.data.materialType !== a.data.materialType)
            return b.data.materialType - a.data.materialType

          // if both have a material order, compare them
          if (
            a.data.materialOrder !== undefined &&
            b.data.materialOrder !== undefined
          )
            return a.data.materialOrder - b.data.materialOrder

          // if only one has a material order, put it first
          if (a.data.materialOrder !== undefined) return -1

          if (b.data.materialOrder !== undefined) return -1

          return 0
        }),
    [materials]
  )

  const { slideDeck } = libraryObject

  return (
    <Dialog size="sm" aria-labelledby={headingId} testId="prework-modal">
      <DialogCloseButton />
      <div className="max-h-[80vh]">
        <h1 id={headingId} className="mb-2 text-headline-large">
          {tt.student_assignment.prework()}
        </h1>
        <p className="mb-5 text-body-large text-on-surface-var">
          {tt.student_assignment.prework_description()}
        </p>
        {cubit.materials.isLoading || libraryObject.slideDeck.isLoading ? (
          <div className="flex flex-grow items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div>
            {preWorkMaterials.map((material) => {
              const { isPodcast, isPDF } = material
              const renderLabel = isPodcast || isPDF
              const label =
                renderLabel &&
                (isPodcast
                  ? tt.student_assignment.prework_listen_to_podcast()
                  : tt.student_assignment.prework_read_case_summary())
              return (
                <>
                  {label && (
                    <div className="mb-3 text-body-medium text-grey-text">
                      {label}
                    </div>
                  )}
                  <CourseMaterialListItem
                    slideDeckVersion={slideDeck.data.slideDeckVersion}
                    className="mb-5 flex h-[62px] flex-row items-center justify-between gap-4 rounded-2xl bg-light-grey p-4"
                    key={material.id}
                    slideDeckName={slideDeck.data.slideDeckName}
                    slideDeckId={slideDeck.id}
                    material={material}
                  />
                </>
              )
            })}
          </div>
        )}
      </div>
    </Dialog>
  )
})
